import { AutocompleteChangeReason, Grid } from "@mui/material";
import React, { useEffect } from "react";
import { Field, Form, Formik } from "formik";
import { Inputs } from "../formInputs";
import { useSelector } from "../../store";
import SearchParams, { defaultParams } from "../../types/searchParams";
import * as Yup from "yup";
import AddHoursFormValues from "../../types/addHoursFormValues";
import { debounce } from "lodash";
import { hourOptions } from "../../types/jobTime";
import { Base } from "../base";
import { AnswerTypes } from "../../types/questionVM";

interface Props {
  apiCategorySearch: (search: SearchParams) => void;
  handleSubmit: (values: AddHoursFormValues) => void;
  handleCancel?: () => void;
  apiGetAllUsers: () => void;
}

const AddHoursForm: React.FC<Props> = (props) => {
  const { handleSubmit, apiCategorySearch, handleCancel, apiGetAllUsers } =
    props;
  const { simpleUserList } = useSelector((store) => store.user);
  const { job } = useSelector((store) => store.job);
  const { currentViewJobType } = useSelector((store) => store.jobType);
  const { categorySearchResults } = useSelector((store) => store.category);
  const { loggedInUser } = useSelector((store) => store.authentication);

  useEffect(() => {
    apiGetAllUsers();
  }, []);

  const validationSchema = Yup.object().shape({
    workedOn: Yup.date().required("Required").typeError("Required"),
    category: Yup.object().required("Required").typeError("Selection Required"),
    workedBy: Yup.object().required("Required").typeError("Selection Required"),
    hours: Yup.object().required("Required").typeError("Selection Required"),
    note: Yup.string().required("Required").typeError("Required"),
  });

  const initialValues: AddHoursFormValues = {
    workedOn: new Date(),
    workedBy: simpleUserList.find((x) => x.id === loggedInUser?.gmdId!),
    hours: undefined,
    category: undefined,
    note: "",
  };

  const updateFilterSearch = debounce((query: string) => {
    apiCategorySearch({
      ...defaultParams,
      query: query,
    });
  }, 500);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        handleSubmit(values);
        resetForm();
      }}
      validationSchema={validationSchema}
      // enableReinitialize
    >
      <Form noValidate>
        <Grid
          container
          direction={"row"}
          justifyContent={"center"}
          columnSpacing={3}
        >
          <Grid size={{ sm: 3, xs: 6 }}>
            <Field
              name="workedOn"
              label="Date Worked"
              component={Inputs.Date}
              required
            />
          </Grid>
          <Grid size={{ sm: 3, xs: 6 }}>
            <Field
              name="category"
              label={
                currentViewJobType?.questions.find(
                  (q) => q.answerType.id === AnswerTypes.Status,
                )?.questionText || "Category"
              }
              component={Inputs.Dropdown}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                updateFilterSearch(e.currentTarget.value);
              }}
              onFocus={() => {
                apiCategorySearch(defaultParams);
              }}
              onSelected={(
                e: React.SyntheticEvent,
                value: any,
                reason: AutocompleteChangeReason,
              ) => {
                if (reason === "clear") {
                  apiCategorySearch(defaultParams);
                }
              }}
              options={categorySearchResults?.results || []}
              required
            />
          </Grid>
          <Grid size={{ sm: 3, xs: 6 }}>
            <Field
              name="workedBy"
              label="Worked By"
              component={Inputs.Dropdown}
              staticFilter
              options={simpleUserList.map((u) => {
                return { id: u.id, value: u.value };
              })}
              required
            />
          </Grid>
          <Grid size={{ sm: 3, xs: 6 }}>
            <Field
              name="hours"
              label="Hours"
              component={Inputs.Dropdown}
              options={hourOptions}
              staticFilter
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field
              name="note"
              label="Work Done"
              component={Inputs.TextArea}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid container direction={"row"} justifyContent={"space-between"}>
              <Grid>
                {handleCancel && (
                  <Base.Button
                    text="Cancel"
                    type="secondary"
                    onClick={handleCancel}
                  />
                )}
              </Grid>
              <Grid>
                <Inputs.Submit
                  text="Add Hours"
                  isSubmitting={false}
                  disabled={job.id === 0}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

export default AddHoursForm;
